import { PaginatedResponse, SamplePack } from '@arpeggi/shared'
import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  useQuery,
  UseQueryOptions
} from 'react-query'
import { ApiService, BasePaginationParams } from '../../features/api'
import { queryClient } from '../../features/react-query-provider/ReactQueryClientProvider'
import {
  CreateSamplePackDto,
  getSamplePackNotifications,
  GetSamplePackNotificationsParams,
  getSamplePacks,
  GetSamplePacksParams,
  UpdateSamplePackDto,
  useSamplePacksAPI
} from '../api/v2/SamplePacksAPI'
import { getNextPage, getPreviousPage } from './common'

export const useSamplePack = ({
  id,
  options
}: {
  id: number
  options?: UseQueryOptions<SamplePack>
}) => {
  return useQuery(['sample-packs', id], () => ApiService.SamplePacks.endpoints.getOne(id), options)
}

export const useSamplePacks = ({
  params,
  options
}: {
  params: GetSamplePacksParams
  options?: UseInfiniteQueryOptions<PaginatedResponse<SamplePack>>
}) => {
  return useInfiniteQuery(
    ['sample-packs', params],
    ({ pageParam = params?.page || 1 }) => getSamplePacks({ ...params, page: pageParam }),
    {
      getPreviousPageParam: (firstPage) => getPreviousPage(firstPage),
      getNextPageParam: (lastPage) => getNextPage(lastPage),
      ...options
    }
  )
}

export const useCreateSamplePack = () => {
  const { createSamplePack } = useSamplePacksAPI()

  return useMutation((dto: CreateSamplePackDto) => createSamplePack(dto), {
    onSuccess: () => queryClient.invalidateQueries(['sample-packs'])
  })
}

export const useUpdateSamplePack = () => {
  const { updateSamplePack } = useSamplePacksAPI()

  return useMutation(
    ({ id, dto }: { id: number; dto: UpdateSamplePackDto }) => updateSamplePack(id, dto),
    {
      onSuccess: () => queryClient.invalidateQueries(['sample-packs'])
    }
  )
}

export const useDeleteSamplePack = () => {
  const { deleteSamplePack } = useSamplePacksAPI()

  return useMutation((id: number) => deleteSamplePack(id), {
    onSuccess: () => queryClient.invalidateQueries(['sample-packs'])
  })
}

export const useSamplePackNotifications = ({
  id,
  params
}: {
  id: number
  params?: GetSamplePackNotificationsParams
}) => {
  return useInfiniteQuery(
    ['sample-packs', id, 'notifications', params],
    ({ pageParam = params?.page || 1 }) =>
      getSamplePackNotifications(id, { ...params, page: pageParam }),
    {
      getPreviousPageParam: getPreviousPage,
      getNextPageParam: getNextPage
    }
  )
}

export const useSamplePackCollaborators = ({
  id,
  params
}: {
  id: number
  params?: BasePaginationParams
}) => {
  return useInfiniteQuery(
    ['sample-packs', id, 'collaborators', params],
    ({ pageParam = params?.page || 1 }) =>
      ApiService.SamplePacks.endpoints.collaborators.getMany(id, { ...params, page: pageParam }),
    {
      getPreviousPageParam: getPreviousPage,
      getNextPageParam: getNextPage
    }
  )
}

export const useSamplePackRemixers = ({
  id,
  params
}: {
  id: number
  params?: BasePaginationParams
}) => {
  return useInfiniteQuery(
    ['sample-packs', id, 'remixers', params],
    ({ pageParam = params?.page || 1 }) =>
      ApiService.SamplePacks.endpoints.remixers.getMany(id, { ...params, page: pageParam }),
    {
      getPreviousPageParam: getPreviousPage,
      getNextPageParam: getNextPage
    }
  )
}

export const useSamplePackRemixes = ({
  id,
  params
}: {
  id: number
  params?: BasePaginationParams
}) => {
  return useInfiniteQuery(
    ['sample-packs', id, 'remixes', params],
    ({ pageParam = params?.page || 1 }) =>
      ApiService.SamplePacks.endpoints.remixes.getMany(id, { ...params, page: pageParam }),
    {
      getPreviousPageParam: getPreviousPage
    }
  )
}
